import { Prettify } from '@xemplo/types';

import type { PayScheduleBase } from '../pay-schedule';

import type { PayrollModelId } from './payrun.constants';

export interface PayrunBase {
  id: string;
  companyId: string;
  businessUnitId: string;
  payScheduleId: string;
  name: string;
  companyName: string;
  businessUnitName: string;
  payDate: Date;
  generatedDateTime: Date;
  generatedDateTimeUtc: Date;
  amendmentDueDateTime: Date;
  amendmentDueDateTimeUtc: Date;
  preparationDueDateTime: Date;
  preparationDueDateTimeUtc: Date;
  approvalDueDateTime: Date;
  approvalDueDateTimeUtc: Date;
  paymentDueDateTime: Date;
  paymentDueDateTimeUtc: Date;
  bankFileUploadDueDateTime: Date;
  bankFileUploadDueDateTimeUtc: Date;
  bankFileCreatedDate: Date;
  createdDateTimeUtc: Date;
  payCycleStartDate: Date;
  payCycleEndDate: Date;
  payrunTypeId: number;
  payrunTypeName: string;
  payrunStatusId: number;
  payrunStatusName: string;
  payOccurrenceName: string;
  isInvoiceNeeded: boolean;
  assigneeUserInfo: AssigneeUserInfo | null;
  milestoneDueDateTime: Date;
  milestoneDueDateTimeUtc: Date;
  finaliseDueDateTime: Date;
  finaliseDueDateTimeUtc: Date;
  payrollProcessingSystemId: PayrollProcessingSystemId;
  payrollProcessingSystemName: string; // e.g. "Keypay", "XemploPay"
}

export type PayrunAssignee = {
  id: string;
  payrunId: string;
  assigneeUserInfo: AssigneeUserInfo | null;
  notes: string;
  createdDateTimeUtc: string;
  assignedByUserInfo: AssigneeUserInfo;
};

export type ExternalPayrun = {
  id: number;
  dateFinalised: string | null;
  payScheduleId: number;
  payPeriodStarting: string;
  payPeriodEnding: string;
  datePaid: string;
  isFinalised: boolean;
  paySlipsPublished: boolean;
  notation: string | null;
  externalId: string | null;
};

/**
 * Request body for `POST /api/v2/payruns/{payrunId}/finalise`. The two flags drive the
 * external finalise: whether to finalise the KeyPay pay run + publish payslips, and
 * whether to send payslips to workers. Both are seeded from the business unit's
 * integration settings but overridable per finalisation. See docs/adr/0008.
 */
export type FinalisePayrunRequestModel = {
  finaliseExternalPayrun: boolean;
  sendPayslips: boolean;
};

/**
 * Result payload of finalising a pay run — the full KeyPay external-finalise output
 * (journal export, payslip publish schedule, notifications, etc.). The FE only consumes
 * the 2xx envelope for success, so the shape is intentionally left opaque.
 */
export type ExternalPayrunFinaliseResult = Record<string, unknown>;

export interface AssigneeUserInfo {
  userId: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
}
export interface PayrunListItem extends PayrunBase {
  amendmentCount: number;
  assigneeUserId: string;
  assigneeType: number;
  payrunApprovers: PayrunListApprover[];
  tags?: string[];
}

export interface PayrunExtended extends PayrunBase {
  payrollModelId: PayrollModelId;
  payrollModelName: string;
  invoiceId: string;
  externalPayrunId: string;
  externalPayrunName: string;
  externalBusinessUnitId: string;
  externalBusinessUnitName: string;
  externalPayScheduleId: string;
  externalPayScheduleName: string;
  payrollSystemConnectionId: number;
  payrollSystemConnectionName: string;
  payrollSystemConfigurationId: number;
  isUploadBankFile: boolean;
  bankFileDateInDays: number;
  isBankFileDatePayDay: boolean;
  bankFileUrl: string;
  isActive: boolean;
  isRejected: boolean;
  rejectDescription: PayrunRejectDescription;
  isPartiallyApproved: boolean;
  isCreateExternalPayrunPending: boolean;
  createExternalPayrunDetails: ExternalPayrunDetails;
}

export interface PayrunListApprover {
  userId: string;
  companyId: string;
  userName: string;
  name: string;
  approverType: string;
  payrunTypeId: number;
  specificPaySchedules: string;
  profilePhoto: string | null;
}

export interface PayrunRejectDescription {
  rejectionReason: PayrunRejectReason;
  user: PayrunRejectUser;
}

export interface PayrunRejectReason {
  rejectReasonId: number;
  errorCategory: string;
  description: string;
}

export interface PayrunRejectUser {
  userId: string;
  userName: string;
  emailAddress: string;
  date: string;
  ipAddress: string;
}

export interface ExternalPayrunDetails {
  payrunId: string;
  externalPayrunId?: string;
  externalPayrunName?: string;
  createPayrunTransactionStatusId?: number;
  createPayrunTransactionStatus?: string;
  createPayrunTransactionError?: string;
}

export enum PayrunTypes {
  SCHEDULED = 1,
  ADHOC = 2,
}

export interface PayrunMilestone {
  amendmentDueDateTime: Date | null;
  amendmentDueDateTimeUtc: Date | null;
  approvalDueDateTime: Date | null;
  approvalDueDateTimeUtc: Date | null;
  id: string;
  name: string;
  payCycleEndDate: Date | null;
  payCycleStartDate: Date | null;
  payDate: Date | null;
  paymentDueDateTime: Date | null;
  paymentDueDateTimeUtc: Date | null;
  preparationDueDateTime: Date | null;
  preparationDueDateTimeUtc: Date | null;
  payrunStatusID: number;
  isInvoiceNeeded: boolean;
  bankFileUploadDueDateTimeUtc: Date | null;
  /** Not yet returned by the API — assumed contract pending BE work */
  finaliseDueDateTimeUtc?: Date | null;
}

export interface PayrunHeader {
  id: string;
  name: string;
  businessUnitId: string;
  businessUnitName: string;
  payScheduleId: string;
  payOccurenceName: string | null;
  payCycleStartDate: Date;
  payCycleEndDate: Date;
  payDate: Date;
  payrunStatusId: number;
  payrunStatusName: string;
  payrunTypeId: number;
  payrunTypeName: string;
  countryId: number;
}
export interface PayrunSummary {
  payrunId: string;
  externalPayRunId: string;
  current: PayrunSummaryItem;
  previous: PayrunSummaryItem | null;
}

export interface PayrunSummaryItem {
  grossPay: number;
  netPay: number;
  totalHours: number;
  superannuation: number;
  totalWithheld: number;
  totalExpenses: number;
}

export interface PayrunChangeLogItem {
  changeStatusDateTime: Date;
  details: string;
  id: number;
  payrunStatusName: string;
  payrunStatusId: number;
  userInfo: PayrunChangeLogUserInfo;
  rejectionDetails: PayrunRejectionDetails;
}

export interface PayrunChangeLogUserInfo {
  userId: string;
  userName: string;
  emailAddress: string;
  date: Date;
  ipAddress: string;
}

export interface PayrunRejectionDetails {
  errorCategory: string;
  reason: string;
  attachment: PayrunRejectionAttachment;
}

export interface PayrunRejectionAttachment {
  id: string;
  displayName: string;
}

export type PayrunEarningLine = {
  payrunId: string;
  externalPayrunId: string;
  externalEmployeeId: string;
  externalEmployeeName: string;
  grossPay: number;
  netPay: number;
  totalTax: number;
  totalWithheld: number;
  superAnnuationEmployerContribution: number;
  superAnnuationEmployeeContribution: number;
  activeAmendmentsCount: number | null;
  variance: number | null;
  previousPayrunId: string | null;
  payscheduleId: string | null;
  warningType: string | null;
  warningDetails: string | null;
};

//PAYRUN_STATUS_ID
export enum PAYRUN_STATUS_IDS {
  GENERATED = 1,
  DETAILS_VIEWED = 2,
  AMENDMENTS_ADDED = 3,
  AMENDEMENTS_SUBMITTED_SYSTEM_TRIGGERED = 4,
  AMENDEMENTS_SUBMITTED_USER_TRIGGERED = 5,
  CONFIRMED_BY_PAYROLL_ADMIN_SYSTEM_TRIGGERED = 6,
  CONFIRMED_BY_PAYROLL_ADMIN_USER_TRIGGERED = 7,
  PREPARED_SYSTEM_TRIGGERED = 8,
  PREPARED_USER_TRIGGERED = 9,
  APPROVED_SYSTEM_TRIGGERED = 10,
  APPROVED_USER_TRIGGERED = 11,
  INVOICE_GENERATED_SYSTEM_TRIGGERED = 12,
  INVOICE_GENERATED_USER_TRIGGERED = 13,
  PAYMENT_PENDING_APPROVAL = 14,
  PAYMENT_CONFIRMED = 15,
  PROCESSING = 16,
  PROCESSED = 17,
  COMPLETED = 18,
  IMPORTING_EARNING_LINES_SYSTEM_TRIGGERED = 19,
  IMPORTING_EARNING_LINES_USER_TRIGGERED = 20,
  IMPORTED_EARNING_LINES_SYSTEM_TRIGGERED = 21,
  IMPORTED_EARNING_LINES_USER_TRIGGERED = 22,
  DRAFT_SYSTEM_TRIGGERED = 23,
  DRAFT_USER_TRIGGERED = 24,
}

export enum PayrunSteps {
  Submit = 'Submit',
  Prepare = 'Prepare',
  Review = 'Review',
  Pending = 'Pending',
  Payment = 'Payment',
  Process = 'Process',
  Complete = 'Complete',
}

/**
 * PEO support is priority, outsources payruns with IsInvoiceNeeded = false shouldn't exist in the current flow but in the off chance they do this will support it.
 * BE will refactor the way it progresses payrun steps so this won't be needed in the future.
 * Payruns with InvoiceNeeded = false will skip steps [10, 11, 12, 13] (all the pending/payment steps)
 */
export const StatusToStepMap = (isInvoiceNeeded: boolean) =>
  isInvoiceNeeded
    ? //isInvoiceNeeded = true
      {
        [PayrunSteps.Submit]: [1, 2, 3],
        [PayrunSteps.Prepare]: [4, 5, 6, 7, 19, 20, 21, 22],
        [PayrunSteps.Review]: [8, 9],
        [PayrunSteps.Pending]: [10, 11, 12, 13],
        [PayrunSteps.Payment]: [14],
        [PayrunSteps.Process]: [15, 16],
        [PayrunSteps.Complete]: [17],
      }
    : //isInvoiceNeeded = false
      {
        [PayrunSteps.Submit]: [1, 2, 3],
        [PayrunSteps.Prepare]: [4, 5, 6, 7, 19, 20, 21, 22],
        [PayrunSteps.Review]: [8, 9],
        [PayrunSteps.Pending]: [],
        [PayrunSteps.Payment]: [],
        [PayrunSteps.Process]: [15, 16, 10, 11, 12, 13, 14],
        [PayrunSteps.Complete]: [17],
      };

export type Step = {
  step: PayrunSteps;
  payrunStatusId: number;
  date: Date | null;
  active: boolean;
  completed: boolean;
  isUtcDate: boolean;
};

export interface MappedPayrunSteps extends PayrunMilestone {
  steps: Step[];
  activeStep: Step | null;
}

export const PayrunsTableType = {
  Active: 'active',
  Complete: 'complete',
  SystemGenerated: 'systemgenerated',
} as const;

export type PayrunsTableType = (typeof PayrunsTableType)[keyof typeof PayrunsTableType];

export type PayrunTableFilterQuery = {
  payrunTypeId?: string;
  milestones?: PayrunSteps[]; // expanded to statusIds at the query boundary
  companyIds?: string[];
  businessUnitIds?: string[];
  assigneeIds?: string[]; //Payroll managers
  primaryOnly?: '1' | '0'; // 1 = true, 0 or missing = false
  payrunAssigneeIds?: string[]; //payrun assignees
  tags?: string[];
  ppsids?: PayrollProcessingSystemId[];
};
export const PayrollProcessingSystemId = {
  KeyPay: 1,
  XemploPay: 2,
} as const;

export type PayrollProcessingSystemId =
  (typeof PayrollProcessingSystemId)[keyof typeof PayrollProcessingSystemId];

export type PayrunApprover = {
  id: string;
  userId: string;
  name?: string;
  userName?: string;
  companyId: string;
  companyName?: string;
  payrunTypeId?: number;
  payrunTypeName?: string;
  payschedules: PayScheduleBase[];
  profilePhoto?: string;
};

export type PayrunApproverCreate = {
  userId: string;
  payrunTypeId: number;
  payscheduleIds: string[];
};

export type CommonPayrun = {
  companyId?: string;
  userId?: string;
};

// CommonPayrun type aliases

export type PayrunApproverDelete = CommonPayrun;
export type PayrunApproverEdit = CommonPayrun;
export type GetPayrunApproverDetails = CommonPayrun;

export type SkipPayrun = {
  payrunIds: string[];
};

export type PayrunApproverUpdate = Prettify<
  Required<CommonPayrun> & {
    payrunTypeId: number;
    payscheduleIds: string[];
  }
>;

export type PreparePayrunCreate = {
  notes?: string;
};
